import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../utils/api';
import ConsumerAuth from '../components/consumer/ConsumerAuth';
import ConsumerDashboard from '../components/consumer/ConsumerDashboard';
import ConsumerBrowse from '../components/consumer/ConsumerBrowse';
import ConsumerFavorites from '../components/consumer/ConsumerFavorites';
import ConsumerNotifications from '../components/consumer/ConsumerNotifications';
import ConsumerCheckout from '../components/consumer/ConsumerCheckout';
import ConsumerOrders from '../components/consumer/ConsumerOrders';
import ConsumerSettings from '../components/consumer/ConsumerSettings';
import ConsumerRetailers from '../components/consumer/ConsumerRetailers';
import ConsumerRadar from '../components/consumer/ConsumerRadar';
import DeliveryTrackingPage from '../components/consumer/DeliveryTrackingPage';
import VerifyEmailPage from '../components/consumer/VerifyEmailPage';
import HelpPage from '../pages/HelpPage';
import { FloatingHelpButton } from '../components/shared/FloatingHelpButton';
import BuildInfo from '../components/BuildInfo';
import Footer from '../components/legal/Footer';

export default function ConsumerApp() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      const parsedUser = JSON.parse(savedUser);
      if (parsedUser.role === 'DAC') {
        setUser(parsedUser);
      } else {
        localStorage.clear();
      }
    }
    setLoading(false);
  }, []);

  // Update user state when localStorage changes (e.g., after email verification)
  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const handleLogin = (token, userData) => {
    if (userData.role !== 'DAC') {
      throw new Error('Invalid user role for Consumer App');
    }
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    window.dispatchEvent(new Event('auth-changed'));
    setUser(userData);
    navigate('/consumer/dashboard');
  };

  const handleLogout = () => {
    localStorage.clear();
    window.dispatchEvent(new Event('auth-changed'));
    setUser(null);
    navigate('/consumer');
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  // Allow verify-email page without authentication
  if (location.pathname === '/consumer/verify-email') {
    return (
      <>
        <VerifyEmailPage />
        <Footer variant="consumer" />
        <BuildInfo />
      </>
    );
  }

  // Allow help page without authentication
  if (location.pathname === '/consumer/help') {
    return (
      <>
        <HelpPage />
        <Footer variant="consumer" />
        <BuildInfo />
      </>
    );
  }

  if (!user) {
    return (
      <>
        <ConsumerAuth onLogin={handleLogin} />
        <Footer variant="consumer" />
        <BuildInfo />
      </>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<Navigate to="/consumer/dashboard" replace />} />
        <Route path="/dashboard" element={<ConsumerDashboard user={user} onLogout={handleLogout} />} />
        <Route path="/browse" element={<ConsumerBrowse user={user} onLogout={handleLogout} />} />
        <Route path="/favorites" element={<ConsumerFavorites user={user} onLogout={handleLogout} />} />
        <Route path="/notifications" element={<ConsumerNotifications user={user} onLogout={handleLogout} />} />
        <Route path="/checkout" element={<ConsumerCheckout user={user} onLogout={handleLogout} />} />
        <Route path="/orders" element={<ConsumerOrders user={user} onLogout={handleLogout} />} />
        <Route path="/track/:orderId" element={<DeliveryTrackingPage user={user} onLogout={handleLogout} />} />
        <Route path="/retailers" element={<ConsumerRetailers user={user} onLogout={handleLogout} />} />
        <Route path="/radar" element={<ConsumerRadar user={user} onLogout={handleLogout} />} />
        <Route path="/settings" element={<ConsumerSettings user={user} onLogout={handleLogout} />} />
        <Route path="/help" element={<HelpPage />} />
      </Routes>
      <FloatingHelpButton userRole="consumer" />
      <Footer variant="consumer" />
      <BuildInfo />
    </>
  );
}
